import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import makeStyles from '@mui/styles/makeStyles';
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	TablePagination,
	IconButton,
	Tooltip,
} from '@mui/material';
import {
	Delete as DeleteIcon,
	Visibility as VisibilityIcon,
	Edit as EditIcon,
	PersonAdd,
} from '@mui/icons-material';
import { useRecoilState } from 'recoil';

import { getAllCharacters, deleteCharacter } from '../../common/services/characterRequests';
import { CharacterViewer } from '../../common/components';
import { diceResultsState } from '../../common/recoil/UserInfo/atoms';
import { Dices } from '../../components';

const useStyles = makeStyles((theme) => ({
	hidePrevIcon: {
		'& button:has([data-testid="KeyboardArrowLeftIcon"])': {
			display: 'none!important',
		},
	},
	actionButton: {
		marginRight: theme.spacing(1),
	},
}));

const AllCharacters = () => {
	const classes = useStyles();
	const [characters, setCharacters] = useState([]);
	const [characterToView, setCharacterToView] = useState();
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(1);
	const [nextToken, setNextToken] = useState();
	const [diceResults, setDiceResults] = useRecoilState(diceResultsState);

	const navigate = useNavigate();

	useEffect(() => {
		const fetchCharacters = async () => {
			try {
				const response = await getAllCharacters({
					size: rowsPerPage,
					next: nextToken,
				});
				const { Items, NextToken } = JSON.parse(response.body).data.characters;
				setCharacters((prevCharacters) => [...prevCharacters, ...(Items || [])]);
				setNextToken(NextToken || '');
			} catch (error) {
				console.log('Error getting characters', error);
			}
		};

		fetchCharacters();
	}, [rowsPerPage, page]);

	const handleDeleteCharacter = async (characterId) => {
		try {
			await deleteCharacter(characterId);
			setCharacters((prevCharacters) =>
				prevCharacters.filter((character) => character.ID !== characterId)
			);
		} catch (error) {
			console.log('Error deleting character', error);
		}
	};

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setNextToken();
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
		setCharacters([]);
	};

	const formatDate = (date) => {
		return new Date(date).toLocaleString('es-ES', {
			day: 'numeric',
			month: 'numeric',
			year: 'numeric',
			hour: 'numeric',
			minute: 'numeric',
		});
	};

	return (
		<div>
			<CharacterViewer
				minimalCharacter={characterToView}
				onClose={() => setCharacterToView()}
			/>
			<TableContainer component={Paper}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell></TableCell>
							<TableCell>Name</TableCell>
							<TableCell>Template</TableCell>
							<TableCell>Template Version</TableCell>
							<TableCell>Public</TableCell>
							<TableCell>Last Modified</TableCell>
							<TableCell>Actions</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{characters.map((character) => (
							<TableRow key={character.ID}>
								<TableCell>{character.avatar}</TableCell>
								<TableCell>{character.name}</TableCell>
								<TableCell>{character.template.name}</TableCell>
								<TableCell>{character.template.version}</TableCell>
								<TableCell>{character.isPublic ? 'Yes' : 'No'}</TableCell>
								<TableCell>{formatDate(character.modifiedDate)}</TableCell>
								<TableCell>
									<Tooltip title="View">
										<IconButton
											className={classes.actionButton}
											onClick={() => setCharacterToView(character)}>
											<VisibilityIcon />
										</IconButton>
									</Tooltip>
									<Tooltip title="Edit">
										<IconButton
											className={classes.actionButton}
											onClick={() => navigate(`/character/${character.ID}`)}>
											<EditIcon />
										</IconButton>
									</Tooltip>
									<Tooltip title="Delete">
										<IconButton
											className={classes.actionButton}
											onClick={() => handleDeleteCharacter(character.ID)}>
											<DeleteIcon />
										</IconButton>
									</Tooltip>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
			<TablePagination
				rowsPerPageOptions={[5, 10, 25]}
				component="div"
				count={nextToken ? -1 : characters.length}
				rowsPerPage={rowsPerPage}
				page={page}
				onPageChange={handleChangePage}
				onRowsPerPageChange={handleChangeRowsPerPage}
				className={classes.hidePrevIcon}
			/>
			<Dices diceResults={diceResults} setDiceResults={setDiceResults}></Dices>
		</div>
	);
};

export default AllCharacters;
